/*************************************** THIRD-PARTY MODULES **************************************/
import React from 'react';
import {withProps} from 'recompose';
import {ToastContainer, ToastMessage} from './react-toastr/src/lib';

import {RequiredInjection} from 'mad-utils/lib/react';

type NotificationTypes = 'error' | 'warning' | 'success' | 'info';

/******************************************** FUNCTIONS *******************************************/
let toastRef;
let toastComp: any;

/**
 * Display a toast of given [type] {error | warning | success | info}, with
 * given [text], and optionally given [title] (Default: ``)
 * Arg order: type, text, title
 *
 * @param {`error`|`warning`|`success`|`info`} type Toast type to show; Affects toast box colour
 * @param {string} text Main text of the toast (the content area)
 * @param {string?} title [OPTIONAL] header to display above the content text {Default: no header}
 */
const addNotification = (type: NotificationTypes, text: string, title: string = ``): void => {
    switch (type) {
        case `error`:
            return toastRef.error(text, title, {});
        case `warning`:
            return toastRef.warning(text, title, {});
        case `success`:
            return toastRef.success(text, title, {});
        case `info`:
        default:
            return toastRef.info(text, title, {});
    }
};

/**
 * Must be run once in the UI tree before toasts can display
 *     <div>
 *         {toastNotificationFactory()}
 *         <SomeComponentThatDisplaysToasts />
 *     </div>
 */
const toastNotificationFactory = (toastLocation = `toast-top-right`): ToastContainer => {
    if (!toastRef) {
        toastComp = (
            <ToastContainer
                ref={toastr => (toastRef = toastr as any)}
                preventDuplicates={true}
                newestOnTop={true}
                className={toastLocation}
                toastMessageFactory={React.createFactory(ToastMessage.animation)}
            />
        );
    }
    return toastComp;
};

/**
 * Any UI tree wrapped in this component can display toasts, via addNotification
 *     <ToastProvider>
 *         <SomeComponentThatCreatesAToast />
 *     </ToastProvider>
 */
export class ToastProvider extends React.Component<{children: React.ReactNode}> {
    render() {
        return (
            <span>
                {toastNotificationFactory()}
                {this.props.children}
            </span>
        );
    }
}

/********************************************* EXPORT *********************************************/
/**
 * Inject methods into class for adding toast notifications, especially
 * this.props.addNotification
 */
export const withToastNotifications = (Component): any =>
    withProps({
        addNotification,
        toastNotificationFactory,
        toast: addNotification,
    })(Component);

export type AddNotificationType = typeof addNotification;
export type ToastNotificationFactoryType = typeof toastNotificationFactory;

export interface ToasterProps {
    addNotification?: RequiredInjection<AddNotificationType>;
    toast?: RequiredInjection<AddNotificationType>;
    toastNotificationFactory?: RequiredInjection<ToastNotificationFactoryType>;
}
